import {
  SUBSCRIPTION_EXPIRY_ALERT_DAYS,
  SUBSCRIPTION_PLANS,
  TRIAL_DAYS,
  type Profile,
  type SubscriptionPlanKey,
} from '@isbd/shared';

export type AccessState =
  | { status: 'staff' }
  | { status: 'trial'; daysLeft: number; endsAt: Date }
  | { status: 'subscribed'; planKey: string; endsOn: string | null; daysLeft: number | null }
  | { status: 'locked'; reason: 'trial_ended' | 'no_subscription' | 'subscription_ended' };

export type ActiveSubscription = {
  plan_type: string;
  status: string;
  ends_on: string | null;
  installments_paid: number;
  installment_count: number;
  next_installment_at: string | null;
};

export type SubscriptionAlert =
  | { kind: 'trial_expiring'; daysLeft: number }
  | { kind: 'subscription_expiring'; daysLeft: number; planKey: string }
  | { kind: 'installment_due'; daysLeft: number; overdue: boolean };

/** Jours restants jusqu’à une date ISO (YYYY-MM-DD), null si absente. */
export function daysUntilDate(
  dateStr: string | null | undefined,
  ref = new Date(),
): number | null {
  if (!dateStr) return null;
  const [y, m, d] = dateStr.split('-').map(Number);
  if (!y || !m || !d) return null;
  const end = new Date(y, m - 1, d);
  const today = new Date(ref);
  today.setHours(0, 0, 0, 0);
  end.setHours(0, 0, 0, 0);
  return Math.ceil((end.getTime() - today.getTime()) / 86400000);
}

/** Jours restants jusqu’à un instant (essai gratuit). */
export function daysUntilTimestamp(iso: string | null | undefined, ref = Date.now()): number | null {
  if (!iso) return null;
  const end = new Date(iso).getTime();
  if (Number.isNaN(end)) return null;
  return Math.max(0, Math.ceil((end - ref) / 86400000));
}

export function isSubscriptionPeriodValid(endsOn: string | null | undefined, ref = new Date()): boolean {
  const days = daysUntilDate(endsOn, ref);
  return days === null || days >= 0;
}

export function computeAccessState(
  profile: Profile | null,
  subscription: ActiveSubscription | null,
): AccessState {
  if (!profile) return { status: 'locked', reason: 'no_subscription' };
  if (profile.role === 'admin' || profile.role === 'advisor' || profile.role === 'jury') {
    return { status: 'staff' };
  }

  if (
    subscription &&
    subscription.status === 'active' &&
    subscription.plan_type !== 'free' &&
    subscription.installments_paid >= 1
  ) {
    if (!isSubscriptionPeriodValid(subscription.ends_on)) {
      return { status: 'locked', reason: 'subscription_ended' };
    }
    return {
      status: 'subscribed',
      planKey: subscription.plan_type,
      endsOn: subscription.ends_on,
      daysLeft: daysUntilDate(subscription.ends_on),
    };
  }

  const endsAt = profile.trial_ends_at
    ? new Date(profile.trial_ends_at)
    : new Date(new Date(profile.created_at).getTime() + TRIAL_DAYS * 86400000);

  if (endsAt.getTime() > Date.now()) {
    const daysLeft = daysUntilTimestamp(profile.trial_ends_at ?? endsAt.toISOString()) ?? 0;
    return { status: 'trial', daysLeft: Math.max(1, daysLeft), endsAt };
  }

  return { status: 'locked', reason: 'trial_ended' };
}

export function getSubscriptionAlert(
  state: AccessState,
  subscription: ActiveSubscription | null,
  alertDays = SUBSCRIPTION_EXPIRY_ALERT_DAYS,
): SubscriptionAlert | null {
  if (state.status === 'trial' && state.daysLeft <= alertDays) {
    return { kind: 'trial_expiring', daysLeft: state.daysLeft };
  }

  if (
    state.status === 'subscribed' &&
    state.daysLeft != null &&
    state.daysLeft <= alertDays
  ) {
    return {
      kind: 'subscription_expiring',
      daysLeft: state.daysLeft,
      planKey: state.planKey,
    };
  }

  const needsInstallment =
    subscription &&
    subscription.status === 'active' &&
    subscription.installments_paid < subscription.installment_count &&
    subscription.next_installment_at;

  if (needsInstallment) {
    const days = daysUntilDate(subscription.next_installment_at);
    if (days !== null && days <= alertDays) {
      return { kind: 'installment_due', daysLeft: days, overdue: days < 0 };
    }
  }

  return null;
}

export function hasFeatureAccess(state: AccessState) {
  return state.status === 'staff' || state.status === 'trial' || state.status === 'subscribed';
}

export function isAllowedWhenLocked(pathname: string) {
  const allowed = ['/app/abonnement', '/app/profil', '/app/mot-de-passe'];
  return allowed.some((p) => pathname === p || pathname.startsWith(`${p}/`));
}

export function planLabel(key: string, locale: 'fr' | 'en' = 'fr') {
  const plan = SUBSCRIPTION_PLANS.find((p) => p.key === (key as SubscriptionPlanKey));
  if (!plan) return key;
  return locale === 'en' ? plan.labelEn : plan.label;
}

export function formatSubscriptionDate(
  dateStr: string | null | undefined,
  locale: 'fr' | 'en' = 'fr',
): string {
  if (!dateStr) return '—';
  const days = daysUntilDate(dateStr);
  if (days === null) return dateStr;
  const [y, m, d] = dateStr.split('-').map(Number);
  const date = new Date(y, m - 1, d);
  return date.toLocaleDateString(locale === 'en' ? 'en-GB' : 'fr-FR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}
